import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { hints, type Hint } from '../../data/hints'
import { HintCard } from './HintCard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { CheckCircle2, Circle, Clock, Loader2 } from 'lucide-react'
import type { Todo } from '@/types/conversations'
import type { GroupedTodos } from '@/types/conversations'

// Fisher-Yates shuffle algorithm for randomizing hints
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Select random N hints
function selectRandomHints(hints: Hint[], count: number): Hint[] {
  return shuffleArray(hints).slice(0, count)
}

// Preload images for smoother transitions
function preloadImages(hints: Hint[]) {
  hints.forEach(hint => {
    if (hint.image_url) {
      const img = new Image()
      img.src = hint.image_url
    }
  })
}

interface HintsDisplayProps {
  onClose?: () => void
  onDontShowToday?: () => void
  groupedTodos?: GroupedTodos
  /** Status message shown at the top of the overlay. Defaults to "Cayu is building your app" */
  statusMessage?: string
  /** Whether to show the close button. Defaults to true */
  showCloseButton?: boolean
  /** Callback to open the task board */
  onOpenBoard?: () => void
}

export const HintsDisplay: React.FC<HintsDisplayProps> = ({
  onClose,
  onDontShowToday,
  groupedTodos,
  statusMessage,
  showCloseButton = true,
  onOpenBoard,
}) => {
  const { t } = useTranslation('conversations')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [dontShowForToday, setDontShowForToday] = useState(false)

  // Select random 5 hints on mount and memoize (shuffled order)
  const selectedHints = useMemo(() => selectRandomHints(hints, 5), [])

  // Preload images for smoother transitions
  useEffect(() => {
    preloadImages(selectedHints)
  }, [selectedHints])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % selectedHints.length)
    }, 10000) // Rotate every 10 seconds

    return () => clearInterval(interval)
  }, [selectedHints.length])

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + selectedHints.length) % selectedHints.length)
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % selectedHints.length)
  }

  const handleCloseClick = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmClose = () => {
    setShowConfirmModal(false)
    if (dontShowForToday) {
      onDontShowToday?.()
    } else {
      onClose?.()
    }
    setDontShowForToday(false)
  }

  const handleCancelClose = () => {
    setShowConfirmModal(false)
    setDontShowForToday(false)
  }

  const currentHint = selectedHints[currentIndex]

  // Check if we have any tasks
  const hasTasks = groupedTodos && (
    groupedTodos.pending.length > 0 ||
    groupedTodos.in_progress.length > 0 ||
    groupedTodos.completed.length > 0
  )

  return (
    <div className={`flex h-full w-full bg-gray-100 dark:bg-gray-900 relative transition-all duration-300 ease-in-out ${
      hasTasks ? 'flex-col items-center p-4' : 'items-center justify-center p-8'
    }`}>
      {/* Status message at top */}
      <div className="absolute top-4 left-0 right-0 flex flex-col items-center gap-2 z-20 pointer-events-none">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
          <p className="text-gray-900 dark:text-white text-sm font-medium flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
            </span>
            <span>{statusMessage}</span>
          </p>
        </div>
        {onOpenBoard && (
          <button
            onClick={onOpenBoard}
            className="pointer-events-auto text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            View task board →
          </button>
        )}
      </div>

      {/* Close button */}
      {showCloseButton && (
        <button
          onClick={handleCloseClick}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors z-30"
          aria-label="Close hints"
        >
          <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>
      )}

      {/* Hints section */}
      <div className={`w-full transition-all duration-300 ease-in-out ${
        hasTasks ? 'hidden' : 'flex items-center justify-center flex-1'
      }`}>
        <div className="w-full max-w-md transition-all duration-300 ease-in-out">
          {/* Hint card with slide animation */}
          <div
            key={currentIndex}
            className="animate-slide-in relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Navigation arrows - visible on hover */}
            {!hasTasks && selectedHints.length > 1 && (
              <>
                <button
                  onClick={handlePrevious}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/90 shadow-lg transition-opacity duration-200 ${
                    isHovering ? 'opacity-100' : 'opacity-0'
                  }`}
                  aria-label="Previous hint"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-700" />
                </button>

                <button
                  onClick={handleNext}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/90 shadow-lg transition-opacity duration-200 ${
                    isHovering ? 'opacity-100' : 'opacity-0'
                  }`}
                  aria-label="Next hint"
                >
                  <ChevronRight className="h-5 w-5 text-gray-700" />
                </button>
              </>
            )}

            {/* Hint card */}
            {!hasTasks && currentHint && (
              <HintCard hint={currentHint} />
            )}
          </div>

          {/* Progress bar */}
          {!hasTasks && (
            <div className="mt-4 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                key={currentIndex}
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                style={{ animation: 'progress 10s linear' }}
              />
            </div>
          )}

          {/* Progress dots */}
          {!hasTasks && selectedHints.length > 1 && (
            <div className="flex justify-center gap-2 mt-3">
              {selectedHints.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'w-6 bg-purple-500' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to hint ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tasks section - only visible when tasks exist, always expanded */}
      {hasTasks && groupedTodos && (
        <div className="flex-1 overflow-hidden transition-all duration-300 ease-in-out mt-20">
          <div className="h-full overflow-y-auto px-4 pb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 max-w-md mx-auto">
              {(() => {
                const total = groupedTodos.pending.length + groupedTodos.in_progress.length + groupedTodos.completed.length
                const remaining = groupedTodos.pending.length + groupedTodos.in_progress.length
                const etaMinutes = Math.ceil(remaining * 0.8)
                return (
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('hints.tasks')}</h3>
                      <span className="text-sm text-muted-foreground">
                        ({groupedTodos.completed.length}/{total})
                      </span>
                    </div>
                    {remaining > 0 && (
                      <span className="text-sm text-muted-foreground">{t('hints.eta', { minutes: etaMinutes })}</span>
                    )}
                  </div>
                )
              })()}
              <div className="space-y-3">
                <TasksContent groupedTodos={groupedTodos} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <AlertDialog open={showConfirmModal} onOpenChange={(open) => {
        setShowConfirmModal(open)
        if (!open) setDontShowForToday(false)
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('hints.closeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('hints.closeConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center justify-end space-x-2 py-2">
            <Checkbox
              id="dont-show-today"
              checked={dontShowForToday}
              onCheckedChange={(checked) => setDontShowForToday(checked === true)}
            />
            <label
              htmlFor="dont-show-today"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              {t('hints.dontShowToday')}
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>{t('hints.keepHints')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              {t('hints.closeAnyway')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes progress {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0%);
          }
        }

        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
      `}</style>
    </div>
  )
}

// Helper component to render tasks without collapse/expand
function TasksContent({ groupedTodos }: { groupedTodos: GroupedTodos }) {
  const { t } = useTranslation('conversations')
  return (
    <>
      {/* In Progress */}
      {groupedTodos.in_progress.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Loader2 className="h-4 w-4 text-primary animate-spin" />
            <span className="text-xs font-medium text-foreground">
              {t('hints.inProgress', { count: groupedTodos.in_progress.length })}
            </span>
          </div>
          <div className="space-y-2">
            {groupedTodos.in_progress.map((todo, index) => (
              <TaskItem key={`in-progress-${index}`} todo={todo} status="in_progress" />
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      {groupedTodos.pending.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {t('hints.pending', { count: groupedTodos.pending.length })}
            </span>
          </div>
          <div className="space-y-2">
            {groupedTodos.pending.map((todo, index) => (
              <TaskItem key={`pending-${index}`} todo={todo} status="pending" />
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {groupedTodos.completed.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              {t('hints.completed', { count: groupedTodos.completed.length })}
            </span>
          </div>
          <div className="space-y-2">
            {groupedTodos.completed.map((todo, index) => (
              <TaskItem key={`completed-${index}`} todo={todo} status="completed" />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function TaskItem({ todo, status }: { todo: Todo, status: 'pending' | 'in_progress' | 'completed' }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {status === 'completed' && (
        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
      )}
      {status === 'in_progress' && (
        <Loader2 className="h-4 w-4 text-primary animate-spin flex-shrink-0 mt-0.5" />
      )}
      {status === 'pending' && (
        <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      )}
      <span
        className={`flex-1 ${
          status === 'completed'
            ? 'text-muted-foreground line-through'
            : status === 'in_progress'
            ? 'text-foreground font-medium'
            : 'text-muted-foreground'
        }`}
      >
        {todo.content}
      </span>
    </div>
  )
}
